// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromUsersActions from '../actions/employee.actions';
import { loadCurrentEmployee } from '@fc-store/actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  AuthService,
  Employee,
  MomentService,
  NotificationService,
  setDefaultTimezone,
  UsersService,
} from '@fc-core';
import { EMPTY, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  addEmployeeToGroups,
  addInvite,
  resetEmployeeFilters,
} from '../actions';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import * as fromInvitesActions from '../actions/invite.actions';
import { EmployeeApiService } from '../../employees/services/employee-api.service';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class EmployeeEffects {
  addEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.createEmployeeWithInvite),
      switchMap(({ invite }) =>
        this.usersService.addEmployee(invite).pipe(
          switchMap((employee) => {
            this.notifications.showSuccess('Employee successfully added');
            this.dialog.closeAll();
            if (!invite.platformAccess.includes('no_access')) {
              return [
                fromUsersActions.addEmployeeSuccess({
                  employee: employee,
                }),
                // todo should return invite id from server if sendInvite is true (load invites is temporary) [sc-16958]
                fromInvitesActions.loadInvites(),
                addEmployeeToGroups({
                  employeeId: employee.id,
                  groups: employee.groups,
                }),
                resetEmployeeFilters(),
              ];
            } else {
              return [
                fromUsersActions.addEmployeeSuccess({
                  employee: employee,
                }),
                addEmployeeToGroups({
                  employeeId: employee.id,
                  groups: employee.groups,
                }),
                resetEmployeeFilters(),
              ];
            }
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );

  linkEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.linkEmployee),
      switchMap(({ email, employee }) => {
        if (email) {
          return this.usersService
            .updateEmployee(employee.id, { email: email })
            .pipe(
              switchMap((employeeItem) => {
                this.dialog.closeAll();
                return [
                  fromUsersActions.updateEmployeeSuccess({
                    employee: employeeItem,
                  }),
                  addInvite({
                    invite: {
                      employee: employeeItem.id,
                    },
                  }),
                ];
              }),
              catchError((error) =>
                of(fromUsersActions.employeeAPIError({ error })),
              ),
            );
        } else {
          return [
            addInvite({
              invite: {
                employee: employee.id,
              },
            }),
          ];
        }
      }),
    ),
  );

  addingEmployeeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromUsersActions.addEmployeeSuccess),
        switchMap(({ employee }) =>
          this.router.navigate(['/users/main/users/', employee.id]),
        ),
      ),
    { dispatch: false },
  );

  loadEmployees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUsersActions.loadEmployees),
      switchMap(() =>
        this.usersService.loadEmployees().pipe(
          map(({ list, count }) =>
            fromUsersActions.loadEmployeesSuccess({
              users: list,
              count,
            }),
          ),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    );
  });

  loadEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUsersActions.loadEmployee),
      switchMap(({ id }) =>
        this.usersService.loadEmployee(id).pipe(
          map((employee: Employee) =>
            fromUsersActions.loadEmployeeSuccess({ employee }),
          ),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    );
  });

  unlinkEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.unlinkEmployee),
      switchMap(({ id }) =>
        this.usersService.unlinkEmployee(id).pipe(
          map(() => {
            this.notifications.showSuccess(
              'Employee login successfully disabled',
            );
            this.dialog.closeAll();
            return fromUsersActions.unlinkEmployeeSuccess({ id });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );
  resendPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.resendPassword),
      switchMap(({ email }) =>
        this.usersService.resendUserPassword(email).pipe(
          map(() => {
            this.notifications.showSuccess('Password successfully resent');
            this.dialog.closeAll();
            return fromUsersActions.resendPasswordSuccess({ email });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );
  enableOperator$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUsersActions.enableOperator),
      switchMap(({ employee }) => {
        return this.usersService.enableOperatorForEmployee(employee.id).pipe(
          switchMap(() => {
            return this.employeeService.getEmployeeById(employee.id).pipe(
              map((updatedEmployee) => {
                this.notifications.showSuccess('Operator successfully enabled');
                this.dialog.closeAll();
                return fromUsersActions.enableOperatorSuccess({
                  employee: updatedEmployee,
                });
              }),
            );
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        );
      }),
    );
  });
  disableOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.disableOperator),
      switchMap(({ operatorId, employeeId }) =>
        this.usersService.disableOperatorForEmployee(operatorId).pipe(
          map(() => {
            this.notifications.showSuccess('Operator successfully disabled');
            this.dialog.closeAll();
            this.router.navigate(['/users/main/users/', employeeId]);
            return fromUsersActions.disableOperatorSuccess({ employeeId });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );

  enableAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUsersActions.enableAdmin),
      switchMap(({ employeeId }) => {
        return this.usersService
          .updateEmployee(employeeId, { isAdmin: true })
          .pipe(
            map((updatedEmployee) => {
              this.notifications.showSuccess('Admin access enabled');
              this.dialog.closeAll();
              return fromUsersActions.enableAdminSuccess({
                employee: updatedEmployee,
              });
            }),
            catchError((error) =>
              of(fromUsersActions.employeeAPIError({ error })),
            ),
          );
      }),
    );
  });

  disableAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUsersActions.disableAdmin),
      switchMap(({ employeeId }) => {
        return this.usersService
          .updateEmployee(employeeId, { isAdmin: false })
          .pipe(
            map((updatedEmployee) => {
              this.notifications.showSuccess('Admin access disabled');
              this.dialog.closeAll();
              return fromUsersActions.disableAdminSuccess({
                employee: updatedEmployee,
              });
            }),
            catchError((error) =>
              of(fromUsersActions.employeeAPIError({ error })),
            ),
          );
      }),
    );
  });

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.updateEmployee),
      switchMap(({ id, changes }) =>
        this.usersService.updateEmployee(id, changes).pipe(
          map((employee) => {
            localStorage.setItem('timezone', changes.timezone);
            this.momentService.timezone = changes.timezone;
            setDefaultTimezone(changes.timezone);
            this.dialog.closeAll();
            this.notifications.showSuccess('Successfully updated data');
            return fromUsersActions.updateEmployeeSuccess({ employee });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );
  deleteEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.archiveEmployee),
      switchMap(({ employeeId }) =>
        this.usersService.deleteEmployee(employeeId).pipe(
          map(() => {
            this.notifications.showSuccess('Employee successfully archived');
            this.dialog.closeAll();
            this.router.navigate(['/users']);
            return fromUsersActions.archiveEmployeeSuccess({ employeeId });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );
  unarchiveEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUsersActions.unarchiveEmployee),
      switchMap(({ employeeId }) =>
        this.usersService.unarchiveEmployee(employeeId).pipe(
          map(() => {
            this.notifications.showSuccess('Employee successfully unarchived');
            this.dialog.closeAll();
            this.router.navigate(['/users']);
            return fromUsersActions.unarchiveEmployeeSuccess({ employeeId });
          }),
          catchError((error) =>
            of(fromUsersActions.employeeAPIError({ error })),
          ),
        ),
      ),
    ),
  );

  getCurrentEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCurrentEmployee),
      switchMap(() =>
        this.employeeService
          .getEmployeeById(this.auth.tokenData.employee_id)
          .pipe(
            map((employee) =>
              fromUsersActions.loadCurrentEmployeeSuccess({ employee }),
            ),
            catchError((error) =>
              of(fromUsersActions.employeeAPIError({ error })),
            ),
          ),
      ),
    ),
  );

  catchError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromUsersActions.employeeAPIError),
        switchMap((error: { type: string; error: HttpErrorResponse }) => {
          this.notifications.showError(getErrorMessage(error?.error));
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private usersService: UsersService,
    private employeeService: EmployeeApiService,
    private dialog: MatDialog,
    private router: Router,
    private momentService: MomentService,
    private auth: AuthService,
    private notifications: NotificationService,
  ) {}
}
